import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ReactComponent as MoreSmallIcon } from '../icons/more.small.svg';
import { ReactComponent as PlusCircleIcon } from '../icons/plus.circle.svg';
import { useEffect, useRef, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import type { Artist, Member } from '../types/response/ISearchArtistResponse';
import { getLikedArtistMembers } from '../data/repositories/LikedArtistMembers';
import { calculateDayOnly } from '../utils/dDayCalculate'; // import baseUrl from '../utils/baseUrl';
import { getUserLocale } from '../utils/getUserLocale';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { unlikeArtist } from '../data/repositories/UnlikeArtist';
import { useSuccessPhotoCardToast } from '../presentation/template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { Input, useColorMode } from '@chakra-ui/react';
import { likeDateBoxColorStyle } from '../presentation/my-page/components/MyFanDomItem';
import { updateArtistLikedDate } from './ManageArtistPage';
import { isEmpty } from 'lodash';
import { getLikedMembersString } from '../presentation/my-page/utils/MyFanDomItemUtil';
import type { ArtistType } from '../presentation/types/ArtistType';
import { isNativeAndroid, sendToNative } from '../utils/NativeUtil';

interface FanBoxProps {
  member?: string;
  artist: string;
  date: string;
  day: string;
  artistId: string;
  onUnlike: (artistId: string) => Promise<void>;
  setSelectedArtist: (artist: { artistId: string; date: string }) => void;
  setIsChangeLikeArtistDateBottomSheetOpen: (isOpen: boolean) => void;
}

function FanBox({
  member,
  artist,
  date,
  day,
  artistId,
  onUnlike,
  setSelectedArtist,
  setIsChangeLikeArtistDateBottomSheetOpen
}: FanBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  return (
    <div className="w-full py-4 flex flex-row items-center border-b border-separator-light-1 dark:border-separator-dark-1">
      <div className="relative w-[66px] h-[66px] mr-[15px]">
        {/* 배경 */}
        <div
          className="absolute inset-0 rounded-[20px] mix-blend-overlay"
          style={likeDateBoxColorStyle[colorMode].boxColorStyle}
        />
        {/* 텍스트 */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full">
          <span
            className="text-[14px] font-semibold"
            style={{
              backgroundImage: `
                conic-gradient(from 270deg at 50% 50%, #94A5F9 0deg, #5CDBF6 108deg, #FFE175 180deg, #D671F6 288deg, #9F9DF9 360deg)
              `,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t('newSignupCompletePage.fanBox.debutDay', { debutDay: day })}
          </span>
          <span
            className="text-[9px] font-semibold"
            style={{
              background:
                colorMode === 'light' ? 'rgba(135, 29, 173, 0.45)' : 'rgba(222,174,239,0.45)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t('newSignupCompletePage.fanBox.debutDate', { debutDate: date })}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-1 h-full justify-center">
        <div className="flex flex-row items-center gap-0.5">
          {member !== t('myPage.allFan') ? (
            <span className="text-main-light-4 dark:text-main-dark-4 text-[14px] font-normal">
              {t('newSignupCompletePage.fanBox.memberFan', { member })}
            </span>
          ) : (
            <span className="text-main-light-4 dark:text-main-dark-4 text-[14px] font-normal">
              {t('newSignupCompletePage.fanBox.allFan')}
            </span>
          )}
          <img
            src="/assets/icons/like.shape.shine.png"
            alt="like.shape.shine"
            className="w-[16px] h-[16px]"
          />
        </div>
        <span className="text-main-light-2 dark:text-main-dark-2 text-[15px] font-semibold">
          {t('newSignupCompletePage.fanBox.artist', { artist })}
        </span>
      </div>
      <div ref={menuRef} className="relative">
        <MoreSmallIcon
          fill="#666666"
          width={20}
          height={20}
          onClick={() => {
            setIsOpen((o) => !o);
          }}
        />
        {/* 메뉴 컴포넌트 */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
              className="
                absolute
                flex flex-col items-stretch
                max-w-48
                right-[20px] top-[15px]
                mt-1 z-10 py-3
                bg-white dark:bg-backgroundElevated-dark rounded-xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.04)]
                border border-separator1"
            >
              <button
                className="text-text-main-lv1 text-[14px] text-semibold w-full px-[24px] py-[8px] text-left whitespace-nowrap"
                onClick={() => {
                  setSelectedArtist({ artistId, date });
                  setIsChangeLikeArtistDateBottomSheetOpen(true);
                  setIsOpen(false);
                }}
                type="button"
              >
                {t('newSignupCompletePage.fanBox.changeLikeArtistDate')}
              </button>
              <button
                className="text-text-main-lv1 text-[14px] text-semibold w-full px-[24px] py-[8px] text-left whitespace-nowrap"
                onClick={() => navigate('/members/manage')}
                type="button"
              >
                {t('newSignupCompletePage.fanBox.changeFavoriteMember')}
              </button>
              <button
                className="text-text-main-lv1 text-[14px] text-semibold w-full px-[24px] py-[8px] text-left whitespace-nowrap"
                onClick={() => {
                  onUnlike(artistId);
                }}
                type="button"
              >
                {t('newSignupCompletePage.fanBox.dislikeArtist')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function NewSignupCompletePage() {
  const [likedArtistMembers, setLikedArtistMembers] = useState<Record<string, Member[]>>({});
  const [likedArtists, setLikedArtists] = useState<Artist[]>([]);
  const [_locale, setLocale] = useState<string | null>(null);
  const [isChangeLikeArtistDateBottomSheetOpen, setIsChangeLikeArtistDateBottomSheetOpen] =
    useState(false);
  const [selectedArtist, setSelectedArtist] = useState({ artistId: '', date: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const notInitial = location.pathname === '/add-artists-complete';

  const getUserProfile = async () => {
    const { likedArtistMembers, likedArtists } = await getLikedArtistMembers();
    setLikedArtistMembers(likedArtistMembers);
    setLikedArtists(likedArtists);
  };

  const showSuccessToast = useSuccessPhotoCardToast();
  const { colorMode } = useColorMode();

  useEffect(() => {
    const locale = getUserLocale();
    setLocale(locale);
    getUserProfile();
  }, []);

  const handleUnlike = async (artistId: string) => {
    const result = await unlikeArtist(artistId);
    if (!result) {
      alert(t('newSignupCompletePage.fanBox.dislikeArtistError'));
      return;
    }

    setLikedArtists((prev) => prev.filter((a) => a.artistId !== artistId));
    const { [artistId]: _, ...rest } = likedArtistMembers;
    setLikedArtistMembers(rest);
    showSuccessToast('top', t('newSignupCompletePage.fanBox.dislikeArtistSuccess') as string);
  };

  return (
    <div className="relative flex flex-col min-h-screen items-center max-w-[576px] px-[20px]">
      <div className="absolute w-[160px] h-[160px] top-[400px] left-0 z-0">
        <img
          src={`/assets/onboarding/bg_signup_complete_0_${colorMode}.png`}
          alt="signup_complete_0"
        />
      </div>
      <div className="w-[160px] h-[160px] absolute top-[180px] right-0 z-0">
        <img
          src={`/assets/onboarding/bg_signup_complete_1_${colorMode}.png`}
          alt="signup_complete_1"
        />
      </div>
      <img
        src="/assets/onboarding/ClappingHands.png"
        alt="Clapping Hands Light Skin Tone"
        className="z-10 w-[164px] h-[164px] mt-[calc(env(safe-area-inset-top)+43px)] mb-[16px]"
      />
      <div className="z-10 flex justify-center items-center flex-col w-full py-[10px]">
        <span className="text-left text-[26px] font-bold line-height-[120%]">
          {notInitial
            ? t('newSignupCompletePage.notInitialTitle')
            : t('newSignupCompletePage.title')}
        </span>
        <span className="text-main-light-4 leading-[20px] dark:text-main-dark-4 text-center whitespace-pre-line mt-2 text-[15px] break-keep">
          {t('newSignupCompletePage.description')}
        </span>
      </div>
      <div className="z-10 mt-[24px] mb-[60px] w-full px-4 bg-gradient-to-br from-white/50 to-white/10 dark:bg-[linear-gradient(92deg,rgba(21,22,25,0.50)_31.85%,rgba(21,22,25,0.10)_102.92%)] rounded-[20px] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-white/60 dark:outline-backgroundElevated-dark backdrop-blur-[20px] inline-flex flex-col justify-start items-start">
        {/* map 형식으로 */}
        {likedArtists.map((artist) => {
          const memberName = getLikedMembersString(artist as unknown as ArtistType);
          const { openDday, endDday } = calculateDayOnly(artist.debutedAt, artist.debutedAt);
          const days = openDday < 0 ? openDday * -1 : openDday;
          return (
            <FanBox
              key={artist.artistId}
              artistId={artist.artistId}
              artist={artist.fandomName}
              member={memberName}
              date={artist.likedDate ?? artist.debutedAt}
              day={artist.likedDays?.toString() ?? days.toString()}
              onUnlike={handleUnlike}
              setIsChangeLikeArtistDateBottomSheetOpen={setIsChangeLikeArtistDateBottomSheetOpen}
              setSelectedArtist={setSelectedArtist}
            />
          );
        })}
        <button
          className="w-full flex flex-row justify-center items-center gap-2 my-[16px]"
          type="button"
          onClick={() => {
            navigate(`/search-artists${notInitial ? '?notInitial=true' : ''}`, { replace: true });
          }}
        >
          <PlusCircleIcon fill="#3D3D3D" width={24} height={24} />
          <span className="text-main-light-2 dark:text-main-dark-2 text-[15px] font-semibold">
            {t('newSignupCompletePage.fanBox.addFavoriteArtist')}
          </span>
        </button>
      </div>
      <button
        className={`
          relative mt-auto mb-[50px]
          flex justify-center items-center
          z-10 w-full h-[52px] py-[17px] px-[24px] rounded-[52px]
          ${likedArtists.length === 0 ? 'bg-disabled-light dark:bg-disabled-dark' : 'bg-primary-light dark:bg-primary-dark'}
          text-onPrimary text-[17px] font-semibold`}
        disabled={likedArtists.length === 0}
        onClick={() => {
          if (!notInitial) {
            if (isNativeAndroid() && window.Android) {
              // TODO: Android 확인필요
            } else {
              sendToNative('{"type": "login"}');
            }
          }
          navigate('/home', { replace: true });
        }}
        type="button"
      >
        {notInitial ? t('confirm') : t('newSignupCompletePage.fanheartStartButton')}
      </button>
      {isChangeLikeArtistDateBottomSheetOpen && (
        <ChangeLikeArtistDateBottomSheet
          isChangeLikeArtistDateBottomSheetOpen={isChangeLikeArtistDateBottomSheetOpen}
          setIsChangeLikeArtistDateBottomSheetOpen={setIsChangeLikeArtistDateBottomSheetOpen}
          selectedArtist={selectedArtist}
          setSelectedArtist={setSelectedArtist}
        />
      )}
    </div>
  );
}

const ChangeLikeArtistDateBottomSheet = ({
  isChangeLikeArtistDateBottomSheetOpen,
  setIsChangeLikeArtistDateBottomSheetOpen,
  selectedArtist,
  setSelectedArtist
}: {
  isChangeLikeArtistDateBottomSheetOpen: boolean;
  setIsChangeLikeArtistDateBottomSheetOpen: (isOpen: boolean) => void;
  selectedArtist: { artistId: string; date: string };
  setSelectedArtist: (artist: { artistId: string; date: string }) => void;
}) => {
  const dateInputRef = useRef<HTMLInputElement>(null);

  const handleDialogButtonClick = async () => {
    const date = dateInputRef.current?.value as string;
    if (!isEmpty(date) && isValidDate(date)) {
      const response = await updateArtistLikedDate(selectedArtist.artistId, date);
      if (response.ok) {
        setIsChangeLikeArtistDateBottomSheetOpen(false);
        setSelectedArtist({ artistId: '', date: '' });
        window.location.reload();
      }
    } else {
      alert(t('manageArtistPage.pleaseCheckInvalidDateAlert'));
    }
  };

  const isValidDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    return !Number.isNaN(date) && date <= now;
  };

  return (
    <>
      {isChangeLikeArtistDateBottomSheetOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 cursor-default"
          onClick={() => setIsChangeLikeArtistDateBottomSheetOpen(false)}
        />
      )}
      <Sheet
        isOpen={isChangeLikeArtistDateBottomSheetOpen}
        onClose={() => setIsChangeLikeArtistDateBottomSheetOpen(false)}
        detent="content"
      >
        <Sheet.Container
          className="!bg-background-elevated"
          style={{
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px'
          }}
        >
          <Sheet.Header className="pt-[40px] px-6">
            <div className="flex flex-col items-center gap-[8px] leading-none">
              <p className="text-pure-black dark:text-pure-white text-[17px] font-bold">
                {t('newSignupCompletePage.fanBox.changeLikeArtistDatePopupTitle')}
              </p>
              <p className="text-text-main-lv4 text-[15px] font-medium">
                {t('newSignupCompletePage.fanBox.changeLikeArtistDatePopupDescription')}
              </p>
            </div>
          </Sheet.Header>
          <Sheet.Content className="flex flex-col mt-[24px]">
            <div className="px-[24px]">
              <Input
                className="bg-secondary-light dark:bg-secondary-dark focus:border-secondary-light dark:focus:border-secondary-dark hover:bg-pure-white dark:hover:bg-pure-black"
                defaultValue={selectedArtist.date}
                marginY={'12px'}
                borderRadius={'12px'}
                size="md"
                type="date"
                ref={dateInputRef}
              />
            </div>
            <button
              className="bg-primary-light dark:bg-primary-dark text-on-primary w-full pt-[14px] pb-[calc(env(safe-area-inset-bottom)+14px)] px-[24px] h-[52px] text-[17px] font-semibold mt-[22px]"
              type="button"
              onClick={handleDialogButtonClick}
            >
              {t('done')}
            </button>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </>
  );
};
